import { CONFIGS, useConfigQuery } from '@buildwise/libs/configurations';
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import useUserData from 'hooks/useUserData';
import { ReactComponent as ArrowLeftDoubleLineIcon } from 'assets/icons/ArrowLeftDoubleLineIcon.svg';
import { ReactComponent as ArrowRightDoubleLineIcon } from 'assets/icons/ArrowRightDoubleLineIcon.svg';
import { theme } from 'config/theme';
import { Dispatch, SetStateAction, useState } from 'react';
import { FilterType } from 'components/common/filter/Filter';

type PhasesDrawerProps = {
  phasesFilters: FilterType | undefined;
  setFilters: Dispatch<SetStateAction<FilterType[]>>;
};

function PhasesDrawer({ phasesFilters, setFilters }: PhasesDrawerProps) {
  const { companyId } = useUserData();
  const { activeUniqueEntries: phases, config } = useConfigQuery({
    companyId,
    configName: CONFIGS.PROJECT_PHASES.configName,
  });
  const [isCollapsed, setCollapsed] = useState(false);

  return (
    <Drawer
      variant="persistent"
      open
      sx={(theme) => ({
        overflow: 'hidden',
        '& .MuiPaper-root': {
          position: 'initial',
          height: '100%',
          minWidth: isCollapsed ? 50 : 220,
          width: isCollapsed ? 50 : 'fit-content',
          transition: 'width 0.3s',
          border: `1px solid ${theme.palette.custom.divider}`,
          borderLeft: 0,
          my: 1.5,
          borderTopRightRadius: '4px',
        },
      })}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        py={1}
        px={1.5}
        flexDirection={isCollapsed ? 'column-reverse' : 'row'}
        gap={1}
      >
        <Typography
          sx={(theme) => ({
            color: theme.palette.custom.tuna,
            fontSize: 14,
            fontWeight: 700,
            whiteSpace: 'nowrap',
            ...(isCollapsed && {
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
            }),
          })}
        >
          {config?.description || 'Project Phases'}{' '}
          {isCollapsed && phasesFilters?.selectedOptions?.length
            ? `(${phasesFilters.selectedOptions.length})`
            : ''}
        </Typography>
        <IconButton
          size="small"
          title={isCollapsed ? 'Expand' : 'Collapse'}
          onClick={() => setCollapsed((prevState) => !prevState)}
        >
          {isCollapsed ? (
            <ArrowRightDoubleLineIcon color={theme.palette.custom.black} width={24} />
          ) : (
            <ArrowLeftDoubleLineIcon color={theme.palette.custom.black} width={24} />
          )}
        </IconButton>
      </Box>
      {!isCollapsed && (
        <>
          <Divider />
          <Box p={1} py={0} height="80%" overflow="auto">
            <List>
              {phasesFilters?.options.map((phase) => (
                <ListItemButton
                  key={phase.value}
                  selected={phasesFilters?.selectedOptions?.includes(phase.value)}
                  sx={{ my: 0.5 }}
                  onClick={() => {
                    if (phasesFilters?.selectedOptions?.includes(phase.value)) {
                      setFilters((prevState) => [
                        {
                          ...prevState[0],
                          selectedOptions: prevState[0]?.selectedOptions?.filter(
                            (_phase) => _phase !== phase.value,
                          ),
                        },
                      ]);
                    } else {
                      setFilters((prevState) => [
                        {
                          ...prevState[0],
                          selectedOptions: [...(prevState[0].selectedOptions || []), phase.value],
                        },
                      ]);
                    }
                  }}
                >
                  <ListItemText>
                    {phase.label || phase.value} {phase.badgeValue ? `(${phase.badgeValue})` : ''}
                  </ListItemText>
                </ListItemButton>
              ))}
            </List>
          </Box>
        </>
      )}
    </Drawer>
  );
}

export default PhasesDrawer;
